import { ALFactoryGlobal, IAugLoopRuntimeManager, InitializeParameters } from '@augloop/office-online';
import { ISessionCloseMessage, SessionCloseReason } from '@augloop/session-protocol';
import { AddOperation, Document } from '@augloop/types-core';
import { APP_VERSION, APP_INTERNAL_NAME, AUGLOOP_INIT_RETRY_DELAY_MILLISECONDS } from '../../AppConstants';
import { AugloopError, AugloopErrors } from './AugloopErrors';
import { LogEvents } from '../../utils/LogEvents';
import { AugloopKeys } from './telemetry/AugloopKeys';
import { v4 as uuidv4 } from 'uuid';
import { OTelTelemetryLogger } from '../TelemetryLogger/Details/OTelTelemetryLogger';
import { getLoggerInstanceForAugLoop } from '../TelemetryLogger/Details/Logger';
import { DurationMeasureType } from '../interfaces/ILogEvent';
import { LogSingleEvent } from '../TelemetryLogger/LogEvent';
import { LogEventNames, ILogData } from "../interfaces/ITelemetryLogger";
import { LogError } from '../../utils/ErrorTelemetry';
import { ErrorStringFromException, PromiseWithRetry } from '../../utils/utils';
import { EventEmitter } from 'events';
import { IAugloopEventEmitter, AugloopEvent } from '../interfaces/IAugloopEventEmitter';
import { ISession } from '@augloop/runtime-client';
import { enableConversationTranscriptionV3 } from '../../utils/ChangeGate';

export enum AugloopConnectionStatus {
  ReadyToConnect,
  Connecting,
  Connected
}

let augLoopConnectAttempt = 0;
const MAX_RETRY_ATTEMPT_WAITING_FOR_AUGLOOP_TOBE_CONNECTED = 10;

let augloopConnectionStatus: AugloopConnectionStatus = AugloopConnectionStatus.ReadyToConnect;
let alServiceUrl: string | null = null;
const guid: string = uuidv4();
let didSetSendOTelEventCallback: boolean = false;

const augloopEventEmitter: IAugloopEventEmitter = new EventEmitter();

// Create a document tile in the session. This is the root of all subsequent tiles that will be created.
function SubmitDocumentTile(runtimeManager: IAugLoopRuntimeManager) {
  const documentTile = new Document({
    isReadonly: false
  });

  const documentOperation = new AddOperation({
    parentPath: ['session'],
    items: [
      {
        id: 'doc',
        body: documentTile
      }
    ]
  });
  runtimeManager.submitOperation({ operation: documentOperation });
}

function getAugloopRuntimeManager(serviceUrl: string, guid: string, attempt: number): void {
  const alRuntimeManager: IAugLoopRuntimeManager = ALFactoryGlobal.getAugLoopRuntimeManager();

  const data: ILogData<AugloopKeys>[] = [{ key: AugloopKeys.CreateAugloopRutimeManagerPromise, value: true }];
  LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, data);

  /**Note: Added a check to only set otelEvent callback once even in retry
   * cases. Else we will get exceptions from ALRunTimeManager - : setSendOTelEventCallback already called.
   **/
  if (didSetSendOTelEventCallback == false) {
    alRuntimeManager.setSendOTelEventCallback({
      sendOTelEvent: (<OTelTelemetryLogger>getLoggerInstanceForAugLoop()).LogEventCallbackForAL,
    });
    didSetSendOTelEventCallback = true;
  }

    const augloopRuntimeConfig: InitializeParameters = {
      serviceUrl,
      appName: APP_INTERNAL_NAME,
      appVersion: APP_VERSION,
      releaseAudienceGroup: '',
      releaseChannel: '',
      releaseFork: '',
      sessionId: guid,
      initSession: true,
      sessionCreationOptions: {
        // Triggered when an AL session connection is established
        onSessionConnect: (_isSeedingRequired: boolean, sessionUrl: string, _origin: string, _authToken: string) => {
          let alSessionKey: string = sessionUrl.substring(sessionUrl.lastIndexOf('/') + 1);
          LogEvents.Augloop.AddData([{ key: AugloopKeys.SessionUrl, value: sessionUrl }]);
          LogEvents.Augloop.AddData([{ key: AugloopKeys.ALSessionKey, value: alSessionKey}]);
          LogEvents.Augloop.AddData([{ key: AugloopKeys.IsSeedingRequired, value: _isSeedingRequired}])
          LogEvents.Augloop.End();
          const data: ILogData<AugloopKeys>[] = [{ key: AugloopKeys.OnSessionConnect, value: true },
                                                 { key: AugloopKeys.SessionConnectAttempt, value: attempt }];
          LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, data);
          augloopEventEmitter.emit(AugloopEvent.SessionConnect);

          if (_isSeedingRequired) {
            SendSeedOperation(alRuntimeManager);
          }
        },

        // Triggered when we disconnected from an AL session (likely due to network issues)
        onSessionDisconnect: (error?: string) => {
          const data: ILogData<AugloopKeys>[] = [{ key: AugloopKeys.OnSessionDisconnect, value: true }];

          if (error)
            data.push({ key: AugloopKeys.OnSessionDisconnectError, value: error });

          LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, data);
          augloopEventEmitter.emit(AugloopEvent.SessionDisconnect);
        },

        //Triggered when a connection is restored after being disconnected.
        onSessionReconnect: () =>  {
          const data: ILogData<AugloopKeys>[] = [{ key: AugloopKeys.OnSessionReconnect, value: true }];
          LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, data);
          augloopEventEmitter.emit(AugloopEvent.SessionReconnect);
        },

        // Triggered when the AL service intentionally closed the sesion.
        onSessionClose: (sessionCloseMessage: ISessionCloseMessage) => {

          // Ignore if close is initiated by the SDX
          if (sessionCloseMessage && sessionCloseMessage.reason) {
            let reason = sessionCloseMessage.reason;
            if (SessionCloseReason.typeGuard(reason) && reason.reasonDescription === 'ClientRequested') {
              return;
            }
          }

          const data: ILogData<AugloopKeys>[] = [{ key: AugloopKeys.OnSessionClose, value: true }];

          if (sessionCloseMessage.reconnectAllowed !== undefined)
            data.push({ key: AugloopKeys.OnSessionCloseReconnectAllowed, value: sessionCloseMessage.reconnectAllowed });

          if (sessionCloseMessage.reason !== undefined)
            data.push({ key: AugloopKeys.OnSessionCloseReason, value: JSON.stringify(sessionCloseMessage.reason) });

          LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, data);
          augloopEventEmitter.emit(AugloopEvent.SessionClose);

          try {
            alRuntimeManager.startNewSession();
          } catch (e) {
            augloopEventEmitter.emit(AugloopEvent.SessionReConnectFailed);
            LogError(LogEventNames.Augloop, "Failed reconnecting after session close event: " + ErrorStringFromException(e));
          }
        }
      }
    };

    if(enableConversationTranscriptionV3){
      augloopRuntimeConfig.flights = "Microsoft.Office.Augloop.Voice.ConversationTranscription:True";
    }

    alRuntimeManager.initialize(augloopRuntimeConfig);
    SubmitDocumentTile(alRuntimeManager);
}

export function GetAugloopConnectionStatus(): AugloopConnectionStatus {
  return augloopConnectionStatus;
}

// After calling this function, the caller should call EnsureAugloopRuntimeManager and await it before using Augloop
export function StartInitializingRuntimeManager(serviceUrl: string): void {
  if (augloopConnectionStatus != AugloopConnectionStatus.ReadyToConnect) {
    return;
  }
  augloopConnectionStatus = AugloopConnectionStatus.Connecting;
  alServiceUrl = serviceUrl;

  LogEvents.Augloop.Start([{ key: AugloopKeys.SessionGuid, value: guid}, { key: AugloopKeys.ServiceUrl, value: serviceUrl}]);
  LogEvents.Augloop.BeginDurationMeasure(DurationMeasureType.Normal, AugloopKeys.InitializeAugloopRuntimeManagerDuration);

  GetAugloopEventEmitter().on(AugloopEvent.SessionConnect, () => {
    augLoopConnectAttempt = 0;
    augloopConnectionStatus = AugloopConnectionStatus.Connected;
  });

  GetAugloopEventEmitter().on(AugloopEvent.SessionDisconnect, () => {
    augloopConnectionStatus = AugloopConnectionStatus.ReadyToConnect;
  });

  GetAugloopEventEmitter().on(AugloopEvent.SessionClose, () => {
    augloopConnectionStatus = AugloopConnectionStatus.ReadyToConnect;
  });

  GetAugloopEventEmitter().on(AugloopEvent.SessionReconnect, () => {
    augLoopConnectAttempt = 0;
    augloopConnectionStatus = AugloopConnectionStatus.Connected;
  });

  GetAugloopEventEmitter().on(AugloopEvent.SessionReConnectFailed, () => {
    augloopConnectionStatus = AugloopConnectionStatus.ReadyToConnect;
  });

  getAugloopRuntimeManager(alServiceUrl, guid, augLoopConnectAttempt++);
}

export async function waitForAugloopConnectionToBeConnected() {
  return PromiseWithRetry<void>(
    () => {
      if (augloopConnectionStatus == AugloopConnectionStatus.Connected) {
        return Promise.resolve();
      } else {
        return Promise.reject(new AugloopError(AugloopErrors.SessionInitTimeout, 'Augloop connection is not getting established.'));
      }
    },
    MAX_RETRY_ATTEMPT_WAITING_FOR_AUGLOOP_TOBE_CONNECTED,
    AUGLOOP_INIT_RETRY_DELAY_MILLISECONDS
  ).catch((e) => {
    augloopConnectionStatus = AugloopConnectionStatus.ReadyToConnect;
    throw e;
  })
}

export async function EnsureAugloopRuntimeManager() {
  if (augloopConnectionStatus == AugloopConnectionStatus.ReadyToConnect && alServiceUrl != null) {
    augloopConnectionStatus = AugloopConnectionStatus.Connecting;
    getAugloopRuntimeManager(alServiceUrl, guid, augLoopConnectAttempt++);
    await waitForAugloopConnectionToBeConnected();
  }

  // A runtime manager represents a session in augloop, so we need to make sure we have one.
  // It exposes mechanisms to send and receive data from augloop in a session, so the
  // voice-related classes we depend on (VoiceSession, etc) need an instance of the manager
  // to communicate with AL.
  const alRuntimeManager = ALFactoryGlobal.getAugLoopRuntimeManager();

  if (alRuntimeManager == null)
    throw new Error('Augloop runtime manager is not initialized');

  return alRuntimeManager;
}

export function GetAugloopEventEmitter(): IAugloopEventEmitter {
  return augloopEventEmitter;
}

async function SendSeedOperation(alRuntimeManager: IAugLoopRuntimeManager) {
  //To improve performance on AL, submitting a seed operation before sending first signal.
  const session: ISession = await alRuntimeManager.getSession();
  const operation = new AddOperation({
    parentPath: ['session'],
    items: [
      { id: 'doc', body: new Document({ isReadonly: false }) }
    ],
  });
  session.submitSeedOperations([operation]);
}
